import type { EnvelopeKind, LegacyEndpoint, LegacyList, LegacyResult } from './types.js';

/**
 * The one code that means success, on every endpoint and in every envelope.
 *
 * Nothing else generalises: across the document "-2" carries eight different meanings and "2"
 * carries seven, so a failure can only be described using the endpoint's own code table.
 */
const SUCCESS = '1';

/**
 * The API sends `code` as the string "1" in most places and the number 1 in others, and
 * `status` likewise. Comparing without coercing means the same check passes on one endpoint and
 * fails on the next.
 */
export function coerceCode(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

/** `count` is a number in the document, but coerced defensively — it is display data. */
export function coerceCount(value: unknown, fallbackLength: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallbackLength;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function fail(
  endpoint: LegacyEndpoint,
  code: string | undefined,
  body?: Readonly<Record<string, unknown>>,
): LegacyResult<never> {
  const resolved = code ?? 'UNKNOWN';
  return {
    ok: false,
    code: resolved,
    /*
      The endpoint's own table first. For a code the table does not carry, the vendor's own reason
      beats a generic line: `add_packages` documents -2/-3/-4 only as one lumped sentence the
      generator cannot split (duplicate / wrong status / tracking number too long), so the table
      has no entry for any of them — and the response's own `msg_en`/`msg_cn` is the only thing
      that says which one happened. Callers still branch on `code`; this is the text for logs and
      for showing an unregistered refusal as the vendor worded it rather than as a guess.
    */
    message:
      endpoint.codes[resolved] ?? vendorMessage(body) ?? `Unexpected response code ${resolved}`,
  };
}

/** A response's own `msg_en`, else `msg_cn`, when either is a non-empty string. */
function vendorMessage(body: Readonly<Record<string, unknown>> | undefined): string | undefined {
  for (const key of ['msg_en', 'msg_cn'] as const) {
    const value = body?.[key];
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return undefined;
}

/**
 * Turns every envelope shape the API is known to use into one result, so nothing above this layer
 * ever reads `code`, `status`, `row`, `msg` or `data` again.
 *
 * A body that is not an object at all (an HTML error page parsed as text, a bare `null`) fails
 * rather than throwing, because a broken gateway should surface as an error message and not as a
 * crash inside a component.
 */
export function normaliseEnvelope<T>(endpoint: LegacyEndpoint, body: unknown): LegacyResult<T> {
  if (!isRecord(body)) {
    // `noCode` endpoints legitimately return an array as their whole payload.
    if (endpoint.envelope === 'noCode' && Array.isArray(body)) {
      return { ok: true, data: body as T };
    }
    return { ok: false, code: 'MALFORMED', message: 'Response was not a JSON object' };
  }

  const kind: EnvelopeKind = endpoint.envelope;

  if (kind === 'noCode') {
    // No envelope to unwrap and no code to check: the body is the payload.
    return { ok: true, data: body as T };
  }

  if (kind === 'legacyStatusRow') {
    const code = coerceCode(body['status']);
    if (code !== SUCCESS) return fail(endpoint, code);
    const row = body['row'];
    return { ok: true, data: (Array.isArray(row) ? row : (row ?? [])) as T };
  }

  if (kind === 'statusRecord') {
    const refusal = coerceCode(body['code']);
    if (refusal !== undefined && refusal !== SUCCESS) return fail(endpoint, refusal, body);
    const status = coerceCode(body['status']);
    if (status !== SUCCESS) return fail(endpoint, status, body);
    return { ok: true, data: body as T };
  }

  if (kind === 'statusMessage') {
    const status = coerceCode(body['status']);
    const message = typeof body['msg'] === 'string' ? body['msg'].trim() : '';
    if (status === SUCCESS) return { ok: true, data: { message } as T };
    // The vendor's own reason beats the table: status 0 is one code for many different refusals.
    if (message !== '') return { ok: false, code: status ?? 'UNKNOWN', message };
    return fail(endpoint, status);
  }

  const code = coerceCode(body['code']);

  /*
    `dataOnly` sends a code only when it is refusing (see `EnvelopeKind`). Absent means success, so
    the check is "if it said anything, it had better be 1" rather than "it must say 1" — while a
    rejection like `{code: "-2", msg_cn, msg_en}` still fails here and never reaches an adapter.
  */
  if (kind === 'dataOnly') {
    if (code !== undefined && code !== SUCCESS) return fail(endpoint, code, body);
  } else if (code !== SUCCESS) {
    return fail(endpoint, code, body);
  }

  /*
    The body is the payload. Unlike `noCode` the code is checked first, so a refusal cannot reach
    an adapter looking like a record; unlike `codeOnly` the fields survive.
  */
  if (kind === 'flatRecord') {
    return { ok: true, data: body as T };
  }

  if (kind === 'standard' || kind === 'dataOnly') {
    const data = body['data'];
    if (!isRecord(data)) {
      // The call was not refused but the payload is missing — treated as malformed rather than
      // silently empty, since an empty screen with no error is the exact failure this client
      // exists to prevent.
      return { ok: false, code: 'MALFORMED', message: 'Response had code 1 but no data object' };
    }

    const raw = data['list'];

    if (Array.isArray(raw)) {
      const result: LegacyList<unknown> = {
        count: coerceCount(data['count'], raw.length),
        list: raw,
      };
      return { ok: true, data: result as T };
    }

    /*
      Keyed rather than listed. `packages_services` returns `list` as an object keyed by warehouse
      id and `goods_type` keys by the code itself — both documented as ordinary `standard`
      endpoints. Before this branch existed they fell through the array check and came back as an
      empty list under `ok: true`: a blank services picker with nothing to explain it, which is the
      silent-empty-screen failure this client was written to prevent, produced by the client.
    */
    if (isRecord(raw)) {
      const values = Object.values(raw);
      const result: LegacyList<unknown> = {
        count: coerceCount(data['count'], values.length),
        list: values,
        keyed: raw,
      };
      return { ok: true, data: result as T };
    }

    /*
      No `list` at all. Two very different responses land here, and `count` is what tells them
      apart:

      - A list that is genuinely empty still carries its `count` (`{count: 0}`), so it stays an
        empty list. Reading that as one row would invent a package out of the envelope itself.
      - A detail record does not: `package_list` called with an `id` returns the row as `data`,
        the same endpoint serving both list and detail (its title says so — 获取包裹列表/详情).
        That is presented as a one-row list, so a caller needs no second code path for it.
    */
    if ('count' in data) {
      const result: LegacyList<unknown> = { count: coerceCount(data['count'], 0), list: [] };
      return { ok: true, data: result as T };
    }

    const result: LegacyList<unknown> = { count: 1, list: [data] };
    return { ok: true, data: result as T };
  }

  // flatMessage and codeOnly carry no payload; the message is the useful part.
  const message = body['msg_en'] ?? body['msg_cn'] ?? '';
  return { ok: true, data: { message: typeof message === 'string' ? message : '' } as T };
}
